<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters>
        <v-col cols="auto" class="text-left font-weight-medium">
          {{ this.$i18n.t('admin.keyBindings.links') }}
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <base-button
            :disabled="saving"
            @click="dialogAction('add')"
            translation-path="admin.keyBindings.addLink"
            class="mr-0"
          />
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-3">
        <v-col cols="12">
          <ribbon-list :listItems="items" item-title-key="verboseName" style-type="raw">
            <template #itemButtons-replace="{ item, index }">
              <dots-menu :items="dotsItems" v-on="getDotsItemsListeners(item, index)" />
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';
export default {
  name: 'KeyBindingsSettings',
  components: {
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    RibbonList,
  },
  computed: {
    keyBindings: get('admin/settingsValues@:keyBindingsSettingName'),
    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },
    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.keyBindings.actions.${this.currentAction}ElementTitle`),
              body: this.$i18n.t(`admin.keyBindings.actions.confirmElement${this.capitalizeCurrentAction}`, {
                name: this.currentActionData.verboseName,
              }),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`admin.keyBindings.actions.${this.currentAction}ElementTitle`),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'verboseName',
          translationPath: 'admin.keyBindings.actions.elementVerboseName',
          cols: '12',
          rules: [
            v => !!v || 'rules.required',
            v =>
              !this.items.find((item, index) => this.currentActionData?.index !== index && item.verboseName === v) ||
              'rules.verboseNameIsTaken',
          ],
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'url',
          translationPath: 'admin.keyBindings.actions.toolLink',
          cols: '12',
          rules: [v => !!v || 'rules.required', v => this.checkToolLink(v) || 'rules.urlNeedToDirectToSameApp'],
          dataType: {
            name: 'text',
          },
          prependInnerIcon: 'mdi-web',
        },
      ];
      const mainInputDialogInputsDict = {
        add: elementEditInputs,
        edit: elementEditInputs,
      };
      return (mainInputDialogInputsDict[this.currentAction] || []).map(input => {
        return { ...input, value: JSON.parse(JSON.stringify(this.currentActionData?.[input.name] || '')) };
      });
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,
    dialogsActionsData: {
      add: {
        agreePath: 'dialog.agreeAdd',
        component: 'MainInputDialog',
      },
      delete: {
        agreePath: 'dialog.agreeDelete',
        component: 'MainDialog',
      },
      edit: {
        agreePath: 'dialog.agreeSave',
        component: 'MainInputDialog',
      },
    },
    dotsItems: [{ name: 'edit' }, { name: 'delete' }],
    items: [],
    itemsChanged: false,
    saving: false,

    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,

    keyBindingsSettingName: 'key_bindings_tools_enabled',
  }),
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = value;
      const { component } = this.dialogsActionsData[type];
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler() {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}`]();
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    handlerAdd() {
      this.items = [...this.items, ...[this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value')]];
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    handlerDelete() {
      const { verboseName } = this.currentActionData;
      const index = this.items.findIndex(item => item.verboseName === verboseName);
      if (index >= 0) {
        this.$delete(this.items, index);
      }
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    handlerEdit() {
      const { verboseName } = this.currentActionData;
      const index = this.items.findIndex(item => item.verboseName === verboseName);
      if (index >= 0) {
        const item = this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value');
        this.$set(this.items, index, item);
      }
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async save() {
      if (!this.itemsChanged) return;
      this.saving = true;
      try {
        await this.editSingleSetting({
          key: this.keyBindingsSettingName,
          body: {
            value: JSON.parse(JSON.stringify(this.items)),
          },
        });
        this.itemsChanged = false;
      } finally {
        this.saving = false;
      }
    },

    checkToolLink(url) {
      if (url.startsWith(process.env.VUE_APP_HOST)) {
        return true;
      } else if (url.startsWith('http')) {
        return false;
      }
      return true;
    },
    getDotsItemsListeners(item, index) {
      return this.dotsItems.reduce((total, action) => {
        total[action.name] = () => {
          this.dialogAction(action.name, { ...item, index });
        };
        return total;
      }, {});
    },
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.itemsChanged = true;
      },
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.currentAction = '';
          this.currentActionData = undefined;
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
    this.items = JSON.parse(JSON.stringify(this.keyBindings));
    this.$nextTick(() => {
      this.itemsChanged = false;
    });
  },
};
</script>
